<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated slideInDown" leave-active-class="animated fadeOut">
    <div class="report-container" id="report-container">
      <div :class="`report ${pdf ? 'pdf' : ''}`" ref="report" id="report">
        <div :class="`report-header ${pdf ? 'pdf' : ''}`">
          <!-- logo -->
          <img v-if="logo" :src="resolveAssetPath(logo, 'ui/customer')" :class="`logo ${pdf ? 'pdf' : ''}`" ref="logo">
          <!-- title -->
          <h1 class="report-title no-wrap">{{activeEnvironment.reportTitle || game.reportTitle || 'Report'}}</h1>
        </div>

        <!-- name/date -->
        <div :style="{
          display: 'flex',
          'flex-flow': 'row nowrap',
          'justify-content': 'center',
          padding: '4px 50px',
          'font-size': '1.6em',
        }">
          <div class="no-wrap">
            {{username}} has completed the
          </div>
        </div>
        <div :style="{
          display: 'flex',
          'flex-flow': 'row nowrap',
          'justify-content': 'center',
          padding: '4px 50px',
          'font-size': '1.6em',
          'font-weight': 'bold',
        }">
          <div class="no-wrap">
            {{narrative.text}} Scenario
          </div>
        </div>
        <div :style="{
          display: 'flex',
          'flex-flow': 'row nowrap',
          'justify-content': 'center',
          padding: '4px 50px',
          'font-size': '1.6em',
        }">
          <div class="no-wrap">
            On {{date}}
          </div>
        </div>

        <!-- static content -->
        <div :style="{
          padding: '8px 50px',
          'font-size': '1.25em',
        }">
          <div>The public health nursing competencies practiced in this scenario included:</div>
          <br>
          <ul v-if="isCommunity">
            <li>Use an ecological perspective and epidemiological data to identify health risks for a population</li>
            <li>Interpret valid and reliable data that impacts the health of individuals, families, and communities to make comparisons that are understandable to all who were involved in the assessment process</li>
            <li>Compare appropriate data sources in a community</li>
            <li>Use available data and resources related to the determinants of health when planning services for individuals, families, and groups</li>
            <li>Describe the implications and potential impacts of public health programs and policies on individuals, families, and groups within a population</li>
            <li>Use program planning and CBPR to implement strategies to engage marginalized/disadvantaged population groups in making decisions that affect their health and wellbeing</li>
            <li>Apply critical thinking and cultural awareness to communication</li>
            <li>Use input from individuals, families, and groups when planning and delivering health care programs and services</li>
            <li>Create a presentation of targeted health information</li>
            <li>Communicate information to multiple audiences including groups, peer professionals, and agency peers</li>
            <li>Demonstrate the use of evidence-based cultural models in a work environment when providing services to individuals, families, and groups</li>
            <li>Use formal and informal relational networks among community organizations and systems conducive to improving the health of individuals, families, and groups within communities</li>
            <li>Select stakeholders needed to address public health issues impacting the health of individuals, families, and groups within the community</li>
            <li>Use community assets and resources, including the government, private, and non-profit sectors, to promote health and to deliver services to individuals, families, and groups</li>
            <li>Use input from varied sources to structure public health programs and services for individuals, families, and groups</li>
            <li>Identify evidence of the effectiveness of community engagement strategies on individuals, families, and groups</li>
            <li>Use the determinants of health and evidence-based practices from public health and nursing science, when planning health promotion & disease prevention interventions for individuals, families, and groups</li>
            <li>Use evidence‐based practice in population-level programs to contribute to meeting core public health functions and the 10 essential public health services</li>
            <li>Use a wide variety of sources and methods to access public health information (i.e., GIS mapping, Community Health Assessment, local/state/and national sources)</li>
            <li>Use research to inform the practice of public health nursing</li>
            <li>Model public health science skills when working with individuals, families, and groups</li>
            <li>Use public health informatics skills pertaining to public health nursing services of individuals, families & groups</li>
            <li>Demonstrate ethical standards of practice in all aspects of public health and public health nursing as the basis of all interactions with individuals, communities, and organizations</li>
            <li>Apply systems thinking to public health nursing practice with individuals, families, and groups</li>
            <li>Participate in stakeholder meetings to identify a shared vision, values, and principles for community action</li>
          </ul>
          <ul v-if="isSTI">
            <li>Health status and health literacy assessment</li>
            <li>Use an ecological perspective and epidemiological data to identify health risks for a population</li>
            <li>Identify individual and family assets, needs, values, beliefs, resources and relevant environmental factors</li>
            <li>Use a data collection plan that incorporates valid and reliable methods and instruments for collection of qualitative and quantitative data to inform the service for individuals, families, and a community</li>
            <li>Apply ethical, legal, and policy guidelines and principles in the collection, maintenance, use, and dissemination of data and information</li>
            <li>Use evidence-based strategies or promising practices from across disciplines to promote health in communities and populations</li>
            <li>Demonstrate knowledge of laws and regulation relevant to public health nursing services</li>
            <li>Plan public health nursing services consistent with laws and regulations</li>
            <li>Comply with organizational procedures and policies</li>
            <li>Determine the health, literacy, and the health literacy of the population served to guide health promotion and disease prevention activities</li>
            <li>Apply critical thinking and cultural awareness to all communication modes (i.e., verbal, nonverbal, written & electronic) with individuals, the community, and stakeholders</li>
            <li>Use input from individuals, families, and groups when planning and delivering health care programs and services</li>
            <li>Use communication models to communicate with individuals, families, and groups effectively and as a member of the interprofessional team(s) or interdisciplinary partnerships</li>
            <li>Deliver culturally responsive PHN services</li>
            <li>Use formal and informal relational networks among community organizations and systems conducive to improving the health of individuals, families, and groups within communities</li>
            <li>Use evidence‐based practice in population-level programs to contribute to meeting core public health functions and the 10 essential public health services</li>
            <li>Use a wide variety of sources and methods to access public health information (i.e., GIS mapping, Community Health Assessment, local/state/and national sources)</li>
            <li>Demonstrate compliance with the requirements of patient confidentiality and human subject protection</li>
            <li>Demonstrate ethical standards of practice in all aspects of public health and public health nursing as the basis of all interactions with individuals, communities, and organizations</li>
            <li>Identify internal and external factors affecting public health nursing practice and opportunities for interprofessional collaboration</li>
          </ul>
          <ul v-if="isPopulation">
            <li>Assess the health status and health literacy of individuals and families, including determinants of health, using multiple sources of data</li>
            <li>Using ecological perspective and data to ID population health risk</li>
            <li>Select variables that measure health and public health conditions</li>
            <li>Interpret valid and reliable data that impacts the health of individuals, families, and communities to make comparisons that are understandable to all who were involved in the assessment process</li>
            <li>Compare appropriate data sources in a community</li>
            <li>Contribute to comprehensive community health assessments through the application of quantitative and qualitative public health nursing data</li>
            <li>Use formal and informal relational networks among community organizations and systems conducive to improving the health of individuals, families, and groups within communities</li>
            <li>Select stakeholders needed to address public health issues impacting the health of individuals, families, and groups within the community</li>
            <li>Use research to inform the practice of public health nursing</li>
            <li>Use public health informatics skills pertaining to public health nursing services of individuals, families & groups</li>
            <li>Facilitate the development of interprofessional teams and workgroups</li>
          </ul>
          <ul v-if="isWomen">
            <li>Health literacy assessment</li>
            <li>Using ecological perspective and data to ID population health risk</li>
            <li>Identify individual and family assets, needs, values, beliefs, resources and relevant environmental factors</li>
            <li>Use evidence-based strategies or promising practices from across disciplines to promote health in communities and populations</li>
            <li>Comply with organizational policies and procedures</li>
            <li>Determine the health, literacy, and the health literacy of the population served to guide health promotion and disease prevention activities</li>
            <li>Apply critical thinking and cultural awareness to all communication modes (i.e., verbal, non-verbal, written & electronic) with individuals, the community, and stakeholders</li>
            <li>Use input from person/family in planning and delivering services</li>
            <li>Use communication models to communicate with individuals, families, and groups effectively and as a member of the interprofessional team(s) or interdisciplinary partnerships</li>
            <li>Deliver culturally responsive PHN services</li>
            <li>Assist individuals, families, and groups to identify and access necessary community resources or services through the referral and follow-up process</li>
            <li>Use formal and informal relational networks among community organizations and systems conducive to improving the health of individuals, families, and groups within communities</li>
            <li>Use input from varied sources to structure public health programs and services for individuals, families, and groups</li>
            <li>Assess hazards and threats to individuals, families, and populations and reduce their risk of exposure and injury in natural and built environments (i.e., chemicals and products)</li>
          </ul>
          <ul v-if="isSchool">
            <li>Apply ethical, legal, and policy guidelines and principles in the collection, maintenance, use, and dissemination of data and information. (1A8)</li>
            <li>Use evidence-based strategies or promising practices from across disciplines to promote health in communities and populations. (1A11)</li>
            <li>Describe the implications and potential impacts of public health programs and policies on individuals, families, and groups within a population. (2A2)</li>
            <li>Plan public health nursing services consistent with laws and regulations. (2A6b)</li>
            <li>Use communication models to communicate with individuals, families, and groups effectively and as a member of interprofessional team(s) or interdisciplinary partnerships. (3A6)</li>
            <li>Use determinants of health effectively when working with diverse individuals, families, and groups. (4A1)</li>
            <li>Demonstrate the use of evidence-based cultural models in a work environment when providing services to individuals, families, and groups. (4A5)</li>
            <li>Select stakeholders needed to address public health issues impacting the health of individuals, families, and groups within the community. (5A3a)</li>
            <li>Use community assets and resources, including the government, private, and non-profit sectors, to promote health and to deliver services to individuals, families, and groups. (5A5)</li>
            <li>Use the determinants of health and evidence-based practices from public health and nursing science, when planning health promotion & disease prevention interventions for individuals, families, and groups. (6A1)</li>
            <li>Use evidence-based practice in population-level programs to contribute to meeting core public health functions and the 10 essential public health services. (6A3)</li>
            <li>Identify internal and external factors affecting public health nursing practice and opportunities for interprofessional collaboration. (8A4a)</li>
            <li>Facilitate the development of interprofessional teams and workgroups. (8A8)</li>
          </ul>
          <ul v-if="isOutbreak">
            <li>Use an ecological perspective and epidemiological data to identify health risks for a population. (1A2a)</li>
            <li>Interpret valid and reliable data that impacts the health of individuals, families, and communities to make comparisons that are understandable to all who were involved in the assessment process. (1A5)</li>
            <li>Contribute to comprehensive community health assessments through the application of quantitative and qualitative public health nursing data. (1A7)</li>
            <li>Describe the implications and potential impacts of public health programs and policies on individuals, families, and groups within a population. (2A2)</li>
            <li>Use a variety of methods to disseminate public health information to individuals, families, and groups within a population. (3A4)</li>
            <li>Describe the role of public health nursing to internal and external audiences (3A7).</li>
            <li>Assess hazards and threats to individuals, families, and populations and reduce their risk of exposure and injury in natural and built environments (i.e., chemicals and products). (6A2b)</li>
            <li>Use evidence‐based practice in population-level programs to contribute to meeting core public health functions and the 10 essential public health services. (6A3)</li>
            <li>Explain the public health nurse’s role in emergency preparedness and disaster response during public health events (i.e., infectious disease outbreak, natural or made-made disasters). (7A2)</li>
            <li>Facilitate the development of interprofessional teams and workgroups. (8A8)</li>
          </ul>
        </div>

        <!-- divider -->
        <div
          class="primarybox"
          :style="{
            height: '3px',
          }"
        >
        </div>

        <!-- footer message (e.g. displaying ©) -->
        <div :style="{
          padding: '2px 25px',
          width: '100%',
          'border-top': '1px solid white',
          'background-color': 'black',
          color: 'white',
        }">
          {{activeEnvironment.reportFooter}}
        </div>
      </div>
    </div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <div v-if="showDownloading" class="download-message">Downloading!</div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <button v-if="showNextButton"
      class="btn download-button"
      @click.prevent="onClickDownload"
    >Download</button>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <bottom-bar v-if="showNextButton" :next="showNarratorNotification ? onComplete : undefined" :tabindex="3" ref="bottomBar" :noBar="true">
      <span slot="button">
        <span v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
      </span>
    </bottom-bar>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import JsPDF from 'jspdf';
import domtoimage from 'dom-to-image-more';

import Narrator from '../ui/Narrator';
import NarratorNotification from '../ui/NarratorNotification';
import BottomBar from '../ui/BottomBar';

export default {
  // $emit 'input' on complete
  components: {
    Narrator,
    NarratorNotification,
    BottomBar,
  },
  data() {
    return {
      showNextButton: false,
      showDownloading: false,
      showNarrator: false,
      showNarratorNotification: false,
      pdf: false,
      score: 0,
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'activeClass',
      'characters',
      'tooltip',
      'narrative',
      'lastNarrative',
      'nextNarrative',
      'narrativeNavbarText',
      'narrativeText',
      'group',
      'me',
      'metrics',
      'game',
      'dateActiveGameCompleted',
      'challenge'
    ]),
    username() {
      const {
        firstName = '',
        lastName = '',
        email = ''
      } = this.me || {};
      return firstName || lastName ? `${firstName} ${lastName}` : email;
    },
    date() {
      return (this.game.carryOverMetricsFromActive && this.dateActiveGameCompleted != null)
        ? new Date(this.dateActiveGameCompleted).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : ((this.challenge && this.challenge.completed)
          ? new Date(this.challenge.completed).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : ((this.group && this.group.updated)
            ? new Date(this.group.updated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            : new Date(Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })));
    },
    reportFileName() {
      return `${this.narrative.text} Completion Certificate`;
      // return (this.activeClass != null && this.activeClass.studentReportFileName != null)
      //   ? this.activeClass.studentReportFileName
      //   : (this.activeEnvironment.reportTitle || this.game.reportTitle || 'Report');
    },
    logo() {
      return this.activeEnvironment.reportLogo || this.activeEnvironment.creditsLogo || this.activeEnvironment.titleLogo;
    },
    lowerText() {
      return this.narrative.text.toLowerCase();
    },
    isCommunity() {
      return this.lowerText.includes('community');
    },
    isSTI() {
      return this.lowerText.includes('sexually');
    },
    isPopulation() {
      return this.lowerText.includes('population');
    },
    isWomen() {
      return this.lowerText.includes('women');
    },
    isSchool() {
      return this.lowerText.includes('school');
    },
    isOutbreak() {
      return this.lowerText.includes('outbreak');
    },
  },
  methods: {
    ...mapActions([
      'getMyGroup'
    ]),
    async show() {
      this.showNarrator = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      this.showNarratorNotification = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      await Vue.nextTick();
    },
    async reset() {
      await Vue.nextTick();
      await this.show();
    },
    navigate() {
      this.$refs.bottomBar.navigate();
    },
    async onComplete() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        window.dispatchEvent(new Event('click'));
        return;
      }
      this.$emit('input');
    },
    async loadPageData() {
      try {
        const { _id: groupId } = this.group;
        await this.getMyGroup({ groupId });
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.$router.showGameErrorCritical();
      }
    },
    onBeforePrint(mql) {
      if (mql instanceof Event) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      } else if (mql.matches) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      }
    },
    iOS() {
      const platform = navigator.platform || navigator.userAgentData.platform;
      return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ].includes(platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    },
    isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },
    async onClickDownload() {
      if (this.showDownloading) return;
      this.showDownloading = true;
      // PDF download works in all browsers except pre-chromium Edge
      // (fails at domtoimage.toPng, and no replacement seems viable, see above)
      // fix for Edge (for now): make the page printable, and open the print dialog automatically
      // (in response to domtoimage.toPng or any other part of the autodownload throwing any error)
      // The user can print to PDF or actually print. Bonus is you can print in other browsers too.
      // Edge requires the user to manually set the Scale in the print dialog to 50% for it to fit.
      // Note that this is generally no longer a problem with the new chromium-based Edge.
      this.pdf = true;
      try {
        await Vue.nextTick();
        let dataUrl = await domtoimage.toPng(this.$refs.report, { cacheBust: true });
        // redundant calls required to work on Safari... (https://github.com/tsayen/dom-to-image/issues/343)
        const brokenBecauseSafari = this.iOS() || this.isSafari();
        if (brokenBecauseSafari) {
          dataUrl = await domtoimage.toPng(this.$refs.report);
          dataUrl = await domtoimage.toPng(this.$refs.report);
        }
        const doc = new JsPDF('p', 'pt', 'a4');
        doc.addImage(dataUrl, 'PNG', 28, 50, 540, 747);

        // TODO: if no autodownload, no soft-lock (explained below), so soft-lock fix commented out
        doc.save(`${this.reportFileName}.pdf`);
        // Before we download the PDF, we have to consider the possibility that the
        // user's settings are set to view the PDF in the browser first when downloading a PDF
        // (instead of actually downloading it). This can be true in many modern browsers.
        // This is a problem because if it opens in the same page, it destroys the Vue app state.
        // This ends up soft-locking the player such that they can't get past the report page.
        // This problem came to my attention when a Firefox update changed the PDF download behavior
        // to default to viewing it in browser (on the same page) when you initiate a download.
        // To fix this while minimally impacting anyone whose browser actually just downloads stuff
        // when a download is initiated, instead first open a new tab, then artificially initiate
        // the download with a bogus DOM hyperlink element that targets the bloburl output by JsPDF,
        // which either downloads the file or loads the blob viewer. If it doesn't load the
        // blob viewer, we autoclose the tab, leaving you back on the report page. If it does load
        // the blob viewer, the user is left in the blob viewer, where they can either:
        // - close it, returning to the original report tab without downloading, where they can
        //   hit print or refresh to save off the report another way, or continue on with the game.
        // - download/print it (which presents a system dialog, and does carry over the expected
        //   default name) (and then close it to return back to the game).
        // eslint-disable-next-line max-len
        // const win = window.open(`${window.location.origin}/downloadplayerreport/${encodeURIComponent(this.reportFileName)}/${encodeURIComponent(doc.output('bloburl', { filename: `${this.reportFileName}.pdf` }))}`, '_blank');
        // if (win == null) {
        //   doc.save(`${this.reportFileName}.pdf`);
        // }

        this.pdf = false;
        this.showDownloading = false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to download report PDF!', error);
        try {
          this.pdf = true;
          this.selectedTab = 0;
          await Vue.nextTick();
          window.print();
          this.pdf = false;
          this.showDownloading = false;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to open print dialog!', e);
          this.pdf = false;
          this.showDownloading = false;
          this.selectedTab = 0;
        }
      }
    },
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    },
  },
  async mounted() {
    // polyfill for safari but works for others as well
    const mediaQueryList = (window.matchMedia != null) ? window.matchMedia('print') : null;
    if (mediaQueryList != null && mediaQueryList.addListener != null) {
      mediaQueryList.removeListener(this.onBeforePrint);
      mediaQueryList.addListener(this.onBeforePrint);
    } else {
      // what you'd normally use, if you weren't needing to support Safari:
      window.removeEventListener('beforeprint', this.onBeforePrint);
      window.addEventListener('beforeprint', this.onBeforePrint);
    }

    await this.show();

    await new Promise((resolve, reject) => {
      if (this.$refs.logo.complete) { // wait for logo to load
        resolve();
      } else {
        this.$refs.logo.addEventListener('load', resolve);
        this.$refs.logo.addEventListener('error', reject);
      }
    });

    // uncomment to enable autodownload (but re-add soft-lock fix above if you do)
    // await this.onClickDownload();

    this.showNextButton = true;
  },
};
</script>

<style lang="scss" scoped>
@media print {
  body, body * {
    visibility: hidden !important;
    overflow: visible !important;
  }
  #report-container, #report-container * {
    visibility: visible !important;
    overflow: visible !important;
  }
  #report-container {
    left: 8.75% !important;
    top: 0 !important;
    width: 82.5% !important;
    overflow: visible !important;
    z-index: 10 !important;

    // this makes background graphics show up without needing to check the box
    color-adjust: exact !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }
}

.no-wrap {
  white-space: nowrap;
}

.download-button {
  position: absolute;
  bottom: 210px;
  right: 0;
  width: 350px;
  font-size: 28px;
  line-height: 55px;
  margin-right: 45px;
  z-index: 100 !important;
  transition: opacity 0.5s ease-in-out;
}

hr {
  border-top: 1px solid #e9ecef;
}
.report {
  background-color: white;
  color: black;
}
.report.pdf {
  border: solid;
  border-width: 3.5px;
  border-color: var(--primaryColor);
}
.report-container {
  position: absolute;
  width: 1150px;
  height: 850px;
  top: 30px;
  left: 30px;
  z-index: 1;
  overflow: auto;
}

.report-header {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  padding: 0 50px;
  background-color: black;
  color: lightgrey;
}
.logo {
  object-fit: contain;
  height: 90%;
  max-width: 40%;
  z-index: 10;
}
.report-title {
  font-size: 1.96em;
  white-space: pre-wrap;
  text-align: right;
  margin: auto 0;
  max-width: 60%;
  max-height: 100%;
  line-height: 100%;
}

.primarybox {
  background-color: var(--primaryColor);
  color: white;

  color-adjust: exact;
  -webkit-print-color-adjust: exact;
}

// https://stackoverflow.com/a/31633969/10747078
br {
  display: block;
  content: '';
  margin-top: 0.95em;
}

.slideInDown {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1); // out expo
}
.fadeOutUp {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19); // in cubic
}
.narrator-notification {
  bottom: 205px;
}

.download-message {
  position: absolute;
  top: 50%;
  left: 25%;
  z-index: 1000;
  border-radius: 15px;
  border: 3px solid black;
  background-color: var(--primaryColor);
  color: white;
  font-size: 30pt;
  padding: 20px;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeCertificatePHN.vue